// TD: cart cross-sell slider + shipping protection + unified progress/GWP
import { Cart } from "@troop-themes/main";

(() => {
  function themeOn(eventName, handler, namespace) {
    const key = `${eventName}.${namespace}`;
    window.on(key, handler);
    return () => window.off(key, handler);
  }

  function themeOnCartUpdated(cartRoot, handler, namespace) {
    return themeOn(
      "theme:cart:updated",
      (event) => {
        if (event.detail != null && event.detail !== cartRoot) return;
        handler(event);
      },
      namespace,
    );
  }

  function cartPostItems(items, openDrawer = null) {
    return Cart.addItems(
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, sections: Cart.sections }),
      },
      openDrawer,
    );
  }

  if (!customElements.get("td-cart-cross-sell")) {
    const crossSellSelectors = {
      addButton: "[data-td-add-button]",
      addButtonText: "[data-td-add-button-text]",
      apiRoot: "[data-td-cross-sell-api-root]",
      cartLineItem: "[data-td-cart-item][data-product-id]",
      productCard: "[data-td-cross-sell-card]",
      productIdsMarker: "template[data-td-product-ids]",
      navWrapper: "[data-td-swiper-nav]",
      comparePrice: "[data-td-price-compare]",
      currentPrice: "[data-td-price-current]",
      swiperViewport: "[data-swiper]",
      slidesTrack: "[data-td-cross-sell-track]",
      cartBody: ".cart--body",
      slide: "[data-swiper-slide]",
      swiperComponent: "swiper-slider",
      titleDefault: "[data-td-title-default]",
      titleEmptyCart: "[data-td-title-empty]",
      variantSelect: "[data-td-variant-select]",
    };

    const crossSellRecommendationsSectionId = "td-cart-cross-sell-recommendations";

    function productIdsKeyFromApiRoot(root) {
      if (!root) return "";
      return (
        root.getAttribute("data-td-product-ids") ||
        root.querySelector(crossSellSelectors.productIdsMarker)?.getAttribute("data-td-product-ids") ||
        ""
      );
    }

    /** Shared parser for `td-cart-cross-sell-recommendations` HTML (both storefront `?section_id=` and `product_recommendations` responses). */
    function parseCrossSellSectionDocument(htmlText) {
      const doc = new DOMParser().parseFromString(htmlText, "text/html");
      const root = doc.querySelector(crossSellSelectors.apiRoot);
      if (!root) return { slides: [], productIdsKey: "" };
      return {
        slides: [...root.querySelectorAll(crossSellSelectors.slide)].map((node) => node.cloneNode(true)),
        productIdsKey: productIdsKeyFromApiRoot(root),
      };
    }

    class TDCartCrossSell extends HTMLElement {
      static index = 0;

      constructor() {
        super();
        this.instanceId = TDCartCrossSell.index += 1;
        this._onProductAdded = (event) => {
          if (event.detail != null) this.lastAddedVariantId = String(event.detail);
        };
        this._onClick = (event) => this.onAddClick(event);
        this._onVariantChange = (event) => this.onVariantChange(event);
      }

      connectedCallback() {
        this.cartRoot = this.closest("cart-element");
        this.slider = this.querySelector(crossSellSelectors.swiperViewport);
        this.sliderTrack = this.querySelector(crossSellSelectors.slidesTrack);
        this.swiperSlider = this.querySelector(crossSellSelectors.swiperComponent);
        this.nav = this.querySelector(crossSellSelectors.navWrapper);
        this.titleDefaultNode = this.querySelector(crossSellSelectors.titleDefault);
        this.titleEmptyNode = this.querySelector(crossSellSelectors.titleEmptyCart);
        if (!this.cartRoot || !this.slider || !this.sliderTrack || !this.swiperSlider) return;

        this.source = this.dataset.tdSource || "manual";
        this.maxProducts = Number.parseInt(this.dataset.tdMaxProducts, 10) || 6;
        this.showOnEmptyCart = this.dataset.tdShowOnEmpty === "true";
        this.lastAddedVariantId = "";
        this.lastRenderedKey = "";
        this.lastCartCrossSellFingerprint = "";
        this.crossSellSectionId =
          this.dataset.tdCrossSellSectionId || crossSellRecommendationsSectionId;
        this._crossSellUpdateRunning = false;
        this._crossSellUpdateQueued = false;
        this.stampedRefreshTimer = null;

        this.sliderTrack.addEventListener("click", this._onClick);
        this.sliderTrack.addEventListener("change", this._onVariantChange);

        const namespace = `TDCartCrossSell${this.instanceId}`;
        this._unsubs = [
          themeOn("theme:cart:productAdded", this._onProductAdded, namespace),
          themeOnCartUpdated(this.cartRoot, () => this.onCartSectionUpdated(), namespace),
        ];

        this.lastCartCrossSellFingerprint = this.cartCrossSellFingerprint();
        this.hydrateFromSSR();
        if (
          this.source === "search_discovery" &&
          !this.sliderTrack.querySelector(crossSellSelectors.slide)
        ) {
          this.queueCrossSellUpdate();
        }
      }

      disconnectedCallback() {
        this.sliderTrack?.removeEventListener("click", this._onClick);
        this.sliderTrack?.removeEventListener("change", this._onVariantChange);
        this._unsubs?.forEach((unsubscribe) => unsubscribe());
        if (this.stampedRefreshTimer) window.clearTimeout(this.stampedRefreshTimer);
      }

      queueCrossSellUpdate() {
        if (this._crossSellUpdateRunning) {
          this._crossSellUpdateQueued = true;
          return;
        }
        let snapshot = null;
        if (this.source !== "search_discovery") {
          snapshot = this.cartCrossSellFingerprint();
          if (snapshot === this.lastCartCrossSellFingerprint) return;
        }
        this._crossSellUpdateRunning = true;
        const work =
          this.source === "search_discovery"
            ? this.refresh()
            : this.applyCrossSellFragment(snapshot);
        work
          .catch((err) =>
            console.error(
              this.source === "search_discovery"
                ? "Cart cross-sell refresh failed"
                : "Cart cross-sell fragment refresh failed",
              err,
            ),
          )
          .finally(() => {
            this._crossSellUpdateRunning = false;
            if (this._crossSellUpdateQueued) {
              this._crossSellUpdateQueued = false;
              this.queueCrossSellUpdate();
            }
          });
      }

      onCartSectionUpdated() {
        this.queueCrossSellUpdate();
      }

      cartCrossSellFingerprint() {
        const body = this.cartRoot.querySelector(crossSellSelectors.cartBody);
        if (body?.hasAttribute("data-td-cart-product-ids")) {
          const empty = body.getAttribute("data-td-cart-empty") === "true";
          const ids = body.getAttribute("data-td-cart-product-ids") ?? "";
          return `${empty ? "e" : "n"}:${ids}`;
        }
        return '';
      }

      async applyCrossSellFragment(snapshot) {
        const empty = this.isCartEmptyForCrossSell();
        this.swapTitles(empty);

        if (empty && !this.showOnEmptyCart) {
          this.hide();
          this.lastCartCrossSellFingerprint = snapshot;
          return;
        }

        const res = await this.fetchStorefrontCrossSellSectionResponse();
        if (!res.ok) throw new Error(res.statusText);
        if (this.cartCrossSellFingerprint() !== snapshot) return;

        const { slides, productIdsKey } = parseCrossSellSectionDocument(await res.text());
        this.renderSlideElements(slides, productIdsKey);
        this.lastCartCrossSellFingerprint = snapshot;
      }

      fetchStorefrontCrossSellSectionResponse() {
        const rootPath = window.Shopify?.routes?.root ?? "/";
        const url = new URL(rootPath, window.location.href);
        url.searchParams.set("section_id", this.crossSellSectionId);
        return fetch(url.toString());
      }

      hydrateFromSSR() {
        const empty = this.isCartEmptyForCrossSell();
        if (empty && !this.showOnEmptyCart) {
          this.hide();
          return;
        }
        const slides = [...this.sliderTrack.querySelectorAll(crossSellSelectors.slide)];
        this.renderSlideElements(slides, this.readProductIdsKeyFromTrack());
      }

      async refresh() {
        const empty = this.isCartEmptyForCrossSell();
        this.swapTitles(empty);
        if (empty && !this.showOnEmptyCart) {
          this.hide();
          return;
        }
        if (empty && this.showOnEmptyCart) {
          try {
            const res = await this.fetchStorefrontCrossSellSectionResponse();
            if (!res.ok) throw new Error(res.statusText);
            const { slides, productIdsKey } = parseCrossSellSectionDocument(await res.text());
            this.renderSlideElements(slides, productIdsKey);
          } catch (err) {
            console.error("Cart cross-sell empty-cart section fetch failed", err);
            this.hydrateFromSSR();
          }
          return;
        }
        const cartItems = this.getCartItems();
        const { slides, productIdsKey } = await this.fetchRecommendationSlides(cartItems);
        this.renderSlideElements(slides, productIdsKey);
      }

      isCartEmptyForCrossSell() {
        const empty_attr = document.querySelector("[data-cart-empty]")?.getAttribute("data-cart-empty");
        if (empty_attr) return empty_attr === "true";
        return false;
      }

      hide() {
        this.setAttribute("aria-hidden", "true");
        this.slider?.setAttribute("aria-hidden", "true");
        this.nav?.setAttribute("aria-hidden", "true");
        this.lastRenderedKey = "";
      }

      show() {
        this.setAttribute("aria-hidden", "false");
        this.slider?.setAttribute("aria-hidden", "false");
        this.nav?.removeAttribute("aria-hidden");
      }

      swapTitles(isEmpty) {
        const defaultTitle = this.titleDefaultNode;
        const emptyTitle = this.titleEmptyNode;
        if (!defaultTitle || !emptyTitle) return;
        if (isEmpty) {
          defaultTitle.setAttribute("hidden", "hidden");
          emptyTitle.removeAttribute("hidden");
        } else {
          emptyTitle.setAttribute("hidden", "hidden");
          defaultTitle.removeAttribute("hidden");
        }
      }

      getCartItems() {
        return [...this.cartRoot.querySelectorAll(crossSellSelectors.cartLineItem)]
          .map((line) => ({
            productId: Number.parseInt(line.getAttribute("data-product-id"), 10),
            variantId: String(line.getAttribute("data-variant-id") || ""),
          }))
          .filter((line) => !Number.isNaN(line.productId));
      }

      readProductIdsKeyFromTrack() {
        return this.sliderTrack?.querySelector(crossSellSelectors.productIdsMarker)?.getAttribute("data-td-product-ids") ?? "";
      }

      async fetchRecommendationSlides(cartItems) {
        const empty = { slides: [], productIdsKey: "" };
        const seed = this.seedId(cartItems);
        if (!seed) return empty;
        const res = await fetch(
          `${Shopify.routes.product_recommendations}?section_id=${crossSellRecommendationsSectionId}&product_id=${seed}&limit=${this.maxProducts}`,
        );
        if (!res.ok) return empty;
        return parseCrossSellSectionDocument(await res.text());
      }

      seedId(items) {
        if (this.lastAddedVariantId) {
          const match = items.find((line) => line.variantId === this.lastAddedVariantId);
          if (match) return match.productId;
        }
        return items.length ? items[items.length - 1].productId : null;
      }

      renderSlideElements(slides, productIdsKey = "") {
        if (!slides.length) {
          this.hide();
          this.lastRenderedKey = "";
          return;
        }
        this.show();
        if (productIdsKey && productIdsKey === this.lastRenderedKey) {
          this.syncSwiper();
          return;
        }
        this.sliderTrack.replaceChildren(...slides);
        this.lastRenderedKey = productIdsKey;
        this.stampedSoon();
        this.syncSwiper();
      }

      syncSwiper() {
        const swiperInstance = this.swiperSlider.slider;
        if (!swiperInstance || !this.sliderTrack.querySelector(crossSellSelectors.slide)) return;
        swiperInstance.update();
        swiperInstance.slideTo(0, 0, false);
      }

      stampedSoon() {
        if (!this.querySelector(".stamped-product-reviews-badge")) return;
        if (this.stampedRefreshTimer) window.clearTimeout(this.stampedRefreshTimer);
        this.stampedRefreshTimer = window.setTimeout(() => {
          if (!this.isConnected || typeof window.StampedFn?.loadBadges !== "function") return;
          window.StampedFn.loadBadges();
        }, 0);
      }

      onVariantChange(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const select = target.closest(crossSellSelectors.variantSelect);
        if (!select || !(select instanceof HTMLSelectElement)) return;
        const card = select.closest(crossSellSelectors.productCard);
        if (!card) return;
        const addButton = card.querySelector(crossSellSelectors.addButton);
        const addLabel = card.querySelector(crossSellSelectors.addButtonText);
        const priceEl = card.querySelector(crossSellSelectors.currentPrice);
        const compareEl = card.querySelector(crossSellSelectors.comparePrice);
        const selectedOption = select.options[select.selectedIndex];
        if (!addButton || !addLabel || !priceEl || !compareEl) return;
        if (!select.value) {
          addButton.setAttribute("disabled", "disabled");
          addButton.dataset.tdSelectedVariantId = "";
          addLabel.textContent = addButton.dataset.tdChooseSizeText || "Choose size";
          return;
        }
        addButton.dataset.tdSelectedVariantId = select.value;
        addButton.removeAttribute("disabled");
        addLabel.textContent = addButton.dataset.tdAddText || "Add to cart";
        priceEl.innerHTML = selectedOption.dataset.tdPrice || "";
        if (selectedOption.dataset.tdComparePrice) {
          compareEl.hidden = false;
          compareEl.innerHTML = selectedOption.dataset.tdComparePrice;
        } else {
          compareEl.hidden = true;
          compareEl.innerHTML = "";
        }
      }

      async onAddClick(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const addButton = target.closest(crossSellSelectors.addButton);
        if (!addButton) return;
        event.preventDefault();
        if (addButton.hasAttribute("disabled") || addButton.getAttribute("aria-busy") === "true") return;
        const variantId = Number.parseInt(addButton.dataset.tdSelectedVariantId, 10);
        if (Number.isNaN(variantId)) return;
        addButton.setAttribute("aria-busy", "true");
        addButton.setAttribute("disabled", "disabled");
        try {
          await cartPostItems([{ id: variantId, quantity: 1 }], null);
        } catch (err) {
          console.error("Cart cross-sell add to cart failed", err);
        } finally {
          addButton.setAttribute("aria-busy", "false");
          if (addButton.dataset.tdSelectedVariantId) addButton.removeAttribute("disabled");
        }
      }
    }

    customElements.define("td-cart-cross-sell", TDCartCrossSell);
  }

  if (!customElements.get("td-cart-progress")) {
    const progressSelectors = {
      cartBody: ".cart--body",
      gwpActions: "[data-td-gwp-actions]",
      gwpAddButton: "[data-td-gwp-add]",
      gwpCard: "[data-td-gwp-card]",
      gwpLineItem: '[data-td-cart-item][data-td-gwp-line="true"]',
      gwpVariantSelect: "[data-td-gwp-variant-select]",
      gwpWrap: "[data-td-gwp-wrap]",
      progressFill: "[data-td-progress-fill]",
      progressMessage: "[data-td-progress-message]",
      tierIcon: "[data-td-tier-icon]",
    };

    class TDCartProgress extends HTMLElement {
      static index = 0;

      constructor() {
        super();
        this.instanceId = TDCartProgress.index += 1;
        this._progressRafId = null;
        this._gwpMutationInFlight = false;
        this._gwpMutationQueued = false;
        this.busy = false;
        this._autoAddPending = false;
        this._onClick = (event) => this.onClick(event);
        this._onChange = (event) => this.onChange(event);
      }

      scheduleProgressUpdate() {
        if (this._progressRafId != null) return;
        this._progressRafId = requestAnimationFrame(() => {
          this._progressRafId = null;
          if (!this.isConnected) return;
          const state = this.getState();
          this.render(state);
          this.runGwpMutations(state);
        });
      }

      runGwpMutations(state) {
        if (!this.gwpActive || !this.isConnected) return;
        if (this._gwpMutationInFlight) {
          this._gwpMutationQueued = true;
          return;
        }
        this._gwpMutationInFlight = true;

        this.syncMutations(state)
          .catch((error) => console.error("Cart GWP mutation failed", error))
          .finally(() => {
            this._gwpMutationInFlight = false;
            if (!this.isConnected) {
              this._gwpMutationQueued = false;
              return;
            }
            if (this._gwpMutationQueued) {
              this._gwpMutationQueued = false;
              this.runGwpMutations(this.getState());
            }
          });
      }

      getState() {
        const cartTotalCents = this.readCartTotalCents();
        const giftLine = this.giftLineItems()[0] || null;
        const parsedLine = Number.parseInt(giftLine?.getAttribute("data-line-num") || "", 10);
        const giftLineNumber = Number.isFinite(parsedLine) && parsedLine >= 1 ? parsedLine : 0;
        return {
          cartTotalCents,
          giftLineNumber,
          giftInCart: giftLine != null,
          shippingReached:
            this.shippingActive &&
            this.shippingThreshold > 0 &&
            cartTotalCents >= this.shippingThreshold,
          gwpReached:
            this.gwpActive && this.gwpThreshold > 0 && cartTotalCents >= this.gwpThreshold,
          singleAutoVariantId: this.normalizeVariantId(this.singleProductAutoVariantId),
          busy: this.busy,
        };
      }

      render(state) {
        this.syncPrimaryMessage(state.cartTotalCents, state.shippingReached, state.gwpReached);
        this.syncProgressFill(state.cartTotalCents);
        this.syncTierIcons(state.shippingReached, state.gwpReached);

        if (this.gwpWrap) {
          const showGiftPicker = state.gwpReached && !state.giftInCart;
          if (showGiftPicker) {
            this.gwpWrap.removeAttribute("hidden");
            this.gwpWrap.removeAttribute("inert");
            this.gwpWrap.setAttribute("aria-hidden", "false");
          } else {
            this.gwpWrap.setAttribute("aria-hidden", "true");
            this.gwpWrap.setAttribute("hidden", "");
            this.gwpWrap.setAttribute("inert", "");
          }
        }

        if (this.gwpPreview) {
          const hidePreviewStrip = state.giftInCart || !this.gwpActive || state.gwpReached;
          this.gwpPreview.toggleAttribute("hidden", hidePreviewStrip);
        }

        if (this.gwpActive) {
          for (const card of this.querySelectorAll(progressSelectors.gwpCard)) {
            const actions = card.querySelector(progressSelectors.gwpActions);
            if (actions) actions.hidden = state.giftInCart;
            const addButton = card.querySelector(progressSelectors.gwpAddButton);
            if (!addButton) continue;
            const hasVariant = !!addButton.dataset.tdSelectedVariantId;
            const canAdd = state.gwpReached && !state.giftInCart && hasVariant;
            addButton.toggleAttribute("disabled", !canAdd || state.busy);
            const variantSelect = card.querySelector(progressSelectors.gwpVariantSelect);
            if (variantSelect instanceof HTMLSelectElement) {
              variantSelect.toggleAttribute("disabled", state.busy);
            }
          }
        }
      }

      async syncMutations(state) {
        if (!this.gwpActive || !this.isMutationOwner() || !this.isConnected) return;

        if (!state.gwpReached) {
          this._autoAddPending = false;
          if (state.giftInCart) await this.removeGiftLines(state);
          return;
        }

        if (state.giftInCart) return;

        const autoId = state.singleAutoVariantId;
        if (this.gwpProductsCount === 1 && autoId > 0 && !this._autoAddPending) {
          this._autoAddPending = true;
          await this.addGiftVariant(autoId);
        }
      }

      connectedCallback() {
        this.cartRoot = this.closest("cart-element");
        if (!this.cartRoot) return;

        this.progressMessageNode = this.querySelector(progressSelectors.progressMessage);
        this.progressFill = this.querySelector(progressSelectors.progressFill);
        this.gwpWrap = this.querySelector(progressSelectors.gwpWrap);
        this.gwpPreview = this.querySelector("[data-td-gwp-preview]");

        const cfg = JSON.parse(this.getAttribute("data-td-config") || "{}");
        const ship = cfg.shipping || {};
        const gwp = cfg.gwp || {};

        this.moneyFormat = cfg.moneyFormat || "${{amount}}";

        this.shippingActive = ship.active === true;
        this.shippingThreshold = ship.threshold || 0;
        this.shippingBeforeTemplate = ship.beforeTemplate || "";
        this.shippingAfterTemplate = ship.afterTemplate || "";

        this.gwpActive = gwp.active === true;
        this.gwpThreshold = gwp.threshold || 0;
        this.gwpBeforeTemplate = gwp.beforeTemplate || "";
        this.gwpAfterTemplate = gwp.afterTemplate || "";
        this.gwpProductsCount = gwp.productsCount || 0;
        this.singleProductAutoVariantId = Number.parseInt(String(gwp.singleAutoVariantId ?? ""), 10) || 0;

        this.applyLayoutFromConfig();

        if (this.gwpActive) {
          this.addEventListener("click", this._onClick);
          this.addEventListener("change", this._onChange);
        }

        this._unsub = themeOnCartUpdated(
          this.cartRoot,
          () => this.scheduleProgressUpdate(),
          `TDCartProgress${this.instanceId}`,
        );
        this.scheduleProgressUpdate();
      }

      applyLayoutFromConfig() {
        const shippingTierCents =
          this.shippingActive && this.shippingThreshold > 0 ? this.shippingThreshold : 0;
        const gwpTierCents = this.gwpActive && this.gwpThreshold > 0 ? this.gwpThreshold : 0;
        this.dualBar =
          this.shippingActive &&
          this.gwpActive &&
          shippingTierCents > 0 &&
          gwpTierCents > 0;
        if (this.dualBar) {
          this.dualTierLowCents = Math.min(shippingTierCents, gwpTierCents);
          this.dualTierHighCents = Math.max(shippingTierCents, gwpTierCents);
        } else {
          this.dualTierLowCents = 0;
          this.dualTierHighCents = 0;
        }
        this.maxThreshold = Math.max(shippingTierCents, gwpTierCents);
      }

      disconnectedCallback() {
        if (this._progressRafId != null) {
          cancelAnimationFrame(this._progressRafId);
          this._progressRafId = null;
        }
        this.removeEventListener("click", this._onClick);
        this.removeEventListener("change", this._onChange);
        this._unsub?.();
      }

      readCartTotalCents() {
        const cartBody = this.cartRoot.querySelector(progressSelectors.cartBody);
        if (!cartBody) return 0;
        const raw = Number.parseInt(cartBody.getAttribute("data-td-cart-total-cents") || "0", 10);
        return Number.isNaN(raw) ? 0 : raw;
      }

      giftLineItems() {
        return [...this.cartRoot.querySelectorAll(progressSelectors.gwpLineItem)];
      }

      formatMoney(cents) {
        const normalized = Math.max(0, Number.isFinite(cents) ? cents : 0);
        if (typeof window.Shopify?.formatMoney === "function") {
          try {
            return window.Shopify.formatMoney(normalized, this.moneyFormat);
          } catch {}
        }
        return `$${(normalized / 100).toFixed(2)}`;
      }

      resolveMessage(beforeTemplate, afterTemplate, goalReached, thresholdCents, cartTotalCents) {
        if (goalReached) return afterTemplate;
        const remainingCents = Math.max(thresholdCents - cartTotalCents, 0);
        return beforeTemplate.replace("[$]", this.formatMoney(remainingCents));
      }

      syncPrimaryMessage(cartTotalCents, shippingReached, gwpReached) {
        if (!this.progressMessageNode) return;
        if (!this.shippingActive && !this.gwpActive) {
          this.progressMessageNode.textContent = "";
          return;
        }
        if (this.shippingActive && !this.gwpActive) {
          this.progressMessageNode.textContent = this.resolveMessage(
            this.shippingBeforeTemplate,
            this.shippingAfterTemplate,
            shippingReached,
            this.shippingThreshold,
            cartTotalCents,
          );
          return;
        }
        if (this.gwpActive && !this.shippingActive) {
          this.progressMessageNode.textContent = this.resolveMessage(
            this.gwpBeforeTemplate,
            this.gwpAfterTemplate,
            gwpReached,
            this.gwpThreshold,
            cartTotalCents,
          );
          return;
        }
        this.progressMessageNode.textContent = this.dualPrimaryMessageText(
          cartTotalCents,
          shippingReached,
          gwpReached,
        );
      }

      dualPrimaryMessageText(cartTotalCents, shippingReached, gwpReached) {
        const shippingLimitCents = this.shippingThreshold;
        const gwpLimitCents = this.gwpThreshold;
        const buildShippingMessage = () =>
          this.resolveMessage(
            this.shippingBeforeTemplate,
            this.shippingAfterTemplate,
            shippingReached,
            shippingLimitCents,
            cartTotalCents,
          );
        const buildGwpMessage = () =>
          this.resolveMessage(
            this.gwpBeforeTemplate,
            this.gwpAfterTemplate,
            gwpReached,
            gwpLimitCents,
            cartTotalCents,
          );

        if (gwpLimitCents < shippingLimitCents) {
          if (!gwpReached) return buildGwpMessage();
          if (!shippingReached) return buildShippingMessage();
          return this.shippingAfterTemplate;
        }
        if (shippingLimitCents < gwpLimitCents) {
          if (!shippingReached) return buildShippingMessage();
          if (!gwpReached) return buildGwpMessage();
          return this.gwpAfterTemplate;
        }
        if (!shippingReached) return buildShippingMessage();
        if (!gwpReached) return buildGwpMessage();
        return this.shippingAfterTemplate;
      }

      syncProgressFill(cartTotalCents) {
        if (!this.progressFill) return;
        let percent = 0;
        if (this.dualBar && this.dualTierHighCents > 0 && this.dualTierLowCents >= 0) {
          const lowerTierCents = this.dualTierLowCents;
          const upperTierCents = this.dualTierHighCents;
          if (cartTotalCents <= lowerTierCents) {
            percent =
              lowerTierCents > 0 ? Math.min(50, (cartTotalCents * 50) / lowerTierCents) : 0;
          } else {
            const spanBetweenTiersCents = Math.max(upperTierCents - lowerTierCents, 1);
            percent =
              50 +
              Math.min(50, ((cartTotalCents - lowerTierCents) * 50) / spanBetweenTiersCents);
          }
          percent = Math.min(100, percent);
        } else if (this.maxThreshold > 0) {
          percent = Math.min(100, (cartTotalCents * 100) / this.maxThreshold);
        }
        this.progressFill.style.setProperty("--progress-percent", `${percent}%`);
      }

      syncTierIcons(shippingReached, gwpReached) {
        if (!this.dualBar || !this.shippingActive || !this.gwpActive) return;
        for (const icon of this.querySelectorAll(progressSelectors.tierIcon)) {
          const tier = icon.getAttribute("data-td-tier");
          const filled =
            (tier === "shipping" && shippingReached) ||
            (tier === "gwp" && gwpReached);
          icon.setAttribute("data-td-filled", filled ? "true" : "false");
        }
      }

      async removeGiftLines(state) {
        const line = state.giftLineNumber;
        if (line < 1) {
          if (state.giftInCart) {
            console.error("TDCartProgress: gift rows had no valid data-line-num for removal");
          }
          state.gwpReached = false;
          state.giftInCart = false;
          return;
        }
        const response = await fetch(`${Shopify.routes.cart_change}.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ line, quantity: 0, sections: Cart.sections }),
        });
        if (!response.ok) throw new Error(response.statusText);
        state.gwpReached = false;
        state.giftInCart = false;

        const payload = await response.json();
        Cart.fillBasket(payload.items);
        Cart.updateTotals(payload.item_count);
        Cart.updateAllHtml(payload.sections, true);
      }

      normalizeVariantId(variantId) {
        const n =
          typeof variantId === "number" && Number.isFinite(variantId)
            ? Math.trunc(variantId)
            : Number.parseInt(String(variantId), 10);
        return Number.isFinite(n) && n > 0 ? n : 0;
      }

      async addGiftVariant(variantId) {
        const id = this.normalizeVariantId(variantId);
        if (this.busy || id <= 0) return;
        this.setBusy(true);
        try {
          await cartPostItems(
            [{ id, quantity: 1, properties: { _td_gwp: "true" } }],
            null,
          );
        } catch (error) {
          console.error("GWP add to cart failed", error);
        } finally {
          this.setBusy(false);
        }
      }

      setBusy(isBusy) {
        this.busy = isBusy;
        this.setAttribute("aria-busy", isBusy ? "true" : "false");
        for (const button of this.querySelectorAll(progressSelectors.gwpAddButton)) {
          button.toggleAttribute("disabled", isBusy);
        }
        for (const select of this.querySelectorAll(progressSelectors.gwpVariantSelect)) {
          if (select instanceof HTMLSelectElement) select.toggleAttribute("disabled", isBusy);
        }
      }

      isMutationOwner() {
        if (this.cartRoot?.dataset.view === "drawer") return true;
        return this.offsetParent !== null;
      }

      async onClick(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const addButton = target.closest(progressSelectors.gwpAddButton);
        if (!addButton) return;
        event.preventDefault();
        const state = this.getState();
        if (addButton.hasAttribute("disabled") || addButton.getAttribute("aria-busy") === "true" || state.busy)
          return;
        if (state.giftInCart) return;
        if (this.gwpActive && this.gwpThreshold > 0 && state.cartTotalCents < this.gwpThreshold) return;

        const variantId = this.normalizeVariantId(addButton.dataset.tdSelectedVariantId || "");
        if (variantId <= 0) return;
        await this.addGiftVariant(variantId);
      }

      onChange(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const select = target.closest(progressSelectors.gwpVariantSelect);
        if (!select || !(select instanceof HTMLSelectElement)) return;
        const card = select.closest(progressSelectors.gwpCard);
        if (!card) return;
        const addButton = card.querySelector(progressSelectors.gwpAddButton);
        if (!addButton) return;

        const addLabel = addButton.querySelector("[data-td-add-button-text]");
        const addText = addButton.dataset.tdAddText || "Add to cart";
        const chooseText =
          addButton.dataset.tdChooseText || addButton.dataset.tdChooseSizeText || "Choose options";

        if (!select.value) {
          addButton.dataset.tdSelectedVariantId = "";
          if (addLabel) addLabel.textContent = chooseText;
        } else {
          addButton.dataset.tdSelectedVariantId = select.value;
          if (addLabel) addLabel.textContent = addText;
        }
        this.render(this.getState());
      }
    }

    customElements.define("td-cart-progress", TDCartProgress);
  }

  if (!customElements.get("td-cart-shipping-protection")) {
    const shippingSelectors = {
      cartLineItem: "[data-td-cart-item]",
      protectionToggle: "[data-td-shipping-protection-toggle]",
    };

    class TDCartShippingProtection extends HTMLElement {
      static index = 0;

      constructor() {
        super();
        this.instanceId = TDCartShippingProtection.index += 1;
        this.busy = false;
        this._onToggle = (event) => this.toggleLine(event);
      }

      connectedCallback() {
        this.cartRoot = this.closest("cart-element");
        this.toggle = this.querySelector(shippingSelectors.protectionToggle);
        this.variantId = Number.parseInt(this.dataset.tdVariantId, 10);
        if (!this.cartRoot || !this.toggle || Number.isNaN(this.variantId)) return;
        this.toggle.addEventListener("change", this._onToggle);
        this._unsub = themeOnCartUpdated(
          this.cartRoot,
          () => this.syncState(),
          `TDCartShippingProtection${this.instanceId}`,
        );
        this.syncState();
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener("change", this._onToggle);
        this._unsub?.();
      }

      lineCount() {
        return this.cartRoot.querySelectorAll(shippingSelectors.cartLineItem).length;
      }

      syncDrawerSummarySpacer() {
        if (this.cartRoot?.dataset.view !== "drawer") return;
        if (typeof this.cartRoot.updateSummaryHeight !== "function") return;
        window.requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.cartRoot.updateSummaryHeight();
        });
      }

      inCart() {
        if (
          this.cartRoot.querySelector(
            `${shippingSelectors.cartLineItem}[data-variant-id="${this.variantId}"]`,
          )
        )
          return true;
        const qty = Cart.basket.get(this.variantId);
        return typeof qty === "number" && qty > 0;
      }

      setBusy(isBusy) {
        this.busy = isBusy;
        this.setAttribute("aria-busy", isBusy ? "true" : "false");
        this.toggle?.toggleAttribute("disabled", isBusy);
      }

      syncState() {
        const protectionInCart = this.inCart();
        this.dataset.tdInCart = protectionInCart ? "true" : "false";
        this.setAttribute("aria-hidden", this.lineCount() > 0 ? "false" : "true");
        this.toggle.checked = protectionInCart;
        this.syncDrawerSummarySpacer();
      }

      async toggleLine(event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (this.busy) {
          event.preventDefault();
          return;
        }
        this.setBusy(true);
        try {
          if (input.checked) {
            await cartPostItems(
              [{ id: this.variantId, quantity: 1, properties: { _td_shipping_protection: "true" } }],
              null,
            );
          } else {
            const response = await fetch(`${Shopify.routes.cart_update}.js`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ updates: { [this.variantId]: 0 }, sections: Cart.sections }),
            });
            if (!response.ok) throw new Error(response.statusText);
            const payload = await response.json();
            Cart.fillBasket(payload.items);
            Cart.updateTotals(payload.item_count);
            Cart.updateAllHtml(payload.sections, true);
          }
        } catch (err) {
          console.error("Shipping protection update failed", err);
        } finally {
          this.setBusy(false);
          this.syncState();
        }
      }
    }

    customElements.define("td-cart-shipping-protection", TDCartShippingProtection);
  }
})();
